/** Small, short-lived request cache. Create browser instances only; never share
 * customer responses between server-rendering requests. */
export const createRequestCache = <T>({
  ttlMs = 30_000,
  maxEntries = 40,
  abortDelayMs = 750,
  now = () => Date.now(),
} = {}) => {
  type Entry = {
    value?: T
    expiresAt: number
    promise?: Promise<T>
    controller?: AbortController
    consumers: number
    abortTimer?: ReturnType<typeof setTimeout>
  }
  const entries = new Map<string, Entry>()

  const remove = (key: string, entry: Entry) => {
    if (entry.abortTimer) clearTimeout(entry.abortTimer)
    if (entries.get(key) === entry) entries.delete(key)
  }
  const peek = (key: string): T | undefined => {
    const entry = entries.get(key)
    if (!entry || entry.value === undefined) return undefined
    if (entry.expiresAt <= now()) { remove(key, entry); return undefined }
    entries.delete(key)
    entries.set(key, entry)
    return entry.value
  }
  const trim = () => {
    for (const [key, entry] of entries) {
      if (entries.size <= maxEntries) break
      if (!entry.promise) remove(key, entry)
    }
  }
  const subscribe = (key: string, entry: Entry) => {
    if (entry.abortTimer) { clearTimeout(entry.abortTimer); entry.abortTimer = undefined }
    entry.consumers++
    let released = false
    return {
      promise: entry.promise || Promise.resolve(entry.value as T),
      release: () => {
        if (released) return
        released = true
        entry.consumers--
        if (entry.consumers || !entry.promise) return
        // Navigation/StrictMode may immediately attach a new consumer. Its
        // request must survive the old component's cleanup.
        entry.abortTimer = setTimeout(() => {
          if (entry.consumers || !entry.promise) return
          remove(key, entry)
          entry.controller?.abort()
        }, abortDelayMs)
      },
    }
  }
  const join = (key: string) => {
    peek(key) // Discard expired completed entries.
    const entry = entries.get(key)
    return entry ? subscribe(key, entry) : undefined
  }
  const acquire = (key: string, request: (signal: AbortSignal) => Promise<T>) => {
    const existing = join(key)
    if (existing) return existing
    const controller = new AbortController()
    const entry: Entry = { expiresAt: 0, consumers: 0, controller }
    entries.set(key, entry)
    entry.promise = Promise.resolve().then(() => request(controller.signal)).then(value => {
      if (!controller.signal.aborted && entries.get(key) === entry) {
        entry.value = value
        entry.expiresAt = now() + ttlMs
      }
      return value
    }).catch(error => {
      remove(key, entry)
      throw error
    }).finally(() => {
      entry.promise = undefined
      if (entry.abortTimer) clearTimeout(entry.abortTimer)
      trim()
    })
    return subscribe(key, entry)
  }
  return { peek, join, acquire }
}

/** Preserve array grouping/order (AND/OR option groups) and every request flag.
 * Object property order alone has no semantic meaning. */
export const requestCacheKey = (url: string, body: unknown): string => {
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonical)
    if (value && typeof value === 'object') return Object.fromEntries(
      Object.entries(value).filter(([, item]) => item !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, canonical(item)]),
    )
    return value
  }
  return JSON.stringify([url, canonical(body)])
}
